import classnames from "classnames";
import environment from "web/script/modules/environment";
import { gettext } from "web/script/modules/django-i18n";
import history from "web/script/utils/history";
import React, { RefObject, useEffect, useState } from "react";
import withReduxProvider from "web/react/redux-provider";
import { languageIsEnglish } from "web/script/modules/language";
import BrandAdContainer from "web/react/components/brand-ad-container/brand-ad-container";
import DiscoverHeader from "web/react/components/discover-header/discover-header";
import FollowDesignerButton from "web/react/components/follow-designer-button/follow-designer-button";
import Button from "web/react/components/button/button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { ReduxStoreState } from "web/redux/store";
import { useHistoryWatcher } from "web/react/hooks/use-history-watcher/use-history-watcher";
import { useSelector as useReduxSelector } from "react-redux";
import { ProfileProps } from "./profile-header";
import styles from "./profile-header.css";

export interface RelatedBrand {
    image_url: string;
    name: string;
    slug: string;
    url: string;
}
interface ProfileHeaderWithLogoProps {
    title: string;
    profile: ProfileProps;
    followCTARef?: RefObject<HTMLDivElement>;
    isIos: boolean;
    relatedDesigners?: RelatedBrand[];
}

function extractTitleState(state: ReduxStoreState): string {
    let titleState = state.feedFiltersReducer.title;
    if (titleState !== null) {
        return titleState;
    } else {
        return "";
    }
}

export function ProfileHeaderWithLogo({
    title,
    profile,
    followCTARef,
    isIos,
    relatedDesigners,
}: ProfileHeaderWithLogoProps): JSX.Element {
    const [feedTitle, setFeedTitle] = useState(title);
    const feedTitleState = useReduxSelector(extractTitleState);
    const pageSubType = environment.get("pageSubType");
    let historyWatcher = useHistoryWatcher();

    let useIosDUText =
        isIos &&
        pageSubType.startsWith("designer") &&
        environment.getFeature("mg_du_banner_ios_to_app");

    useEffect(() => {
        if (feedTitleState) {
            setFeedTitle(feedTitleState);
        }
    }, [feedTitleState]);

    function handleBrandClick(slug): void {
        let search = window.location.search;
        let newURL: URL;
        let selected = search.includes(slug);

        if (!selected) {
            let path = window.location.pathname;

            // pageSubType will say designer even if it's explore
            let isExplore = path.search("/explore/") !== -1;

            if (isExplore) {
                newURL = new URL(window.location.href);
            } else {
                newURL = new URL("/explore/", window.location.href);
                newURL.searchParams.append("designer_slug", profile.slug);
            }
            newURL.searchParams.append("designer_slug", slug);
        } else {
            newURL = new URL(window.location.href);

            if (newURL.searchParams.getAll("designer_slug").includes(slug)) {
                let newSearch = search.replace(`&designer_slug=${slug}`, "");
                newURL.search = newSearch;
            }
        }
        history.pushState(newURL);
    }

    let urlSearchParamsObj = historyWatcher
        ? new URLSearchParams(historyWatcher.newURL.search)
        : null;

    let designerSlugs: string[] = [];
    urlSearchParamsObj
        ? urlSearchParamsObj.forEach((value: string) => designerSlugs.push(value))
        : [];

    let relatedBrands = relatedDesigners
        ? relatedDesigners.map((brand) => {
              let checked = designerSlugs.includes(brand.slug);
              return { ...brand, checked };
          })
        : [];

    let showDiscoveryFeature =
        environment.getFeature("mg_discover_feature") &&
        languageIsEnglish() &&
        !pageSubType.startsWith("retailer") &&
        !profile.brand_ad;

    let feedsRedesign = environment.getFeature("mg_feeds_redesign");

    return (
        <>
            <div
                className={classnames(styles.wrapper, {
                    [styles.pmt477Design]: profile.brand_ad,
                })}
            >
                <div className={styles.content}>
                    <div
                        className={classnames(styles.feedTitle, {
                            [styles.feedTitleWithFollowButton]: profile.follow_button,
                            [styles.feedTitleWithoutFollowButton]: !profile.follow_button,
                        })}
                    >
                        {profile.small_photo_url && (
                            <div className={styles.icon}>
                                <img
                                    src={profile.small_photo_url}
                                    alt={
                                        // "{designer_name} logotype"
                                        gettext("feed.designer_logo.image_alt_text", {
                                            designer_name: title,
                                        })
                                    }
                                />
                            </div>
                        )}
                        {feedTitle && (
                            <h1
                                className={styles.name}
                                dangerouslySetInnerHTML={{
                                    __html: feedTitle,
                                }}
                            />
                        )}
                    </div>
                    <div className={styles.buttonWrapper}>
                        {languageIsEnglish() && profile.follow_button && (
                            <div
                                ref={followCTARef}
                                className={classnames(styles.followButtonWrapper, {
                                    [styles.followButtonWithDiscovery]: showDiscoveryFeature,
                                })}
                            >
                                <FollowDesignerButton
                                    className={styles.followButton}
                                    followedText={profile.follow_button.followed_text}
                                    notFollowedText={
                                        useIosDUText
                                            ? // "Get designer updates in the Lyst App"
                                              gettext(
                                                  "designer_updates.get_updates_in_app.header_cta"
                                              )
                                            : profile.follow_button.notfollowed_text_top
                                    }
                                    analyticsParameters={["du_feed_cta", "click_top_cta", "du_cta"]}
                                    lystId={profile.follow_button.lyst_id}
                                    isAuthenticated={profile.follow_button.is_authenticated}
                                    isFollowing={profile.follow_button.is_following}
                                />
                            </div>
                        )}
                        {languageIsEnglish() && profile.retailer_hub_link && (
                            <Button
                                styleType="tertiary"
                                className={styles.promos}
                                href={profile.retailer_hub_link}
                                disabled={false}
                                fullWidth={false}
                                fullHeight={false}
                                openInNewTab={false}
                            >
                                <SVGIcon className={styles.promosIcon} name={"tag"} />
                                {// "Promo’s"
                                gettext("promotion_message.designer.promo_btn_text")}
                            </Button>
                        )}
                    </div>
                </div>
                {profile.name && relatedBrands.length > 0 && showDiscoveryFeature && (
                    <div className={styles.discoverHeader}>
                        <DiscoverHeader
                            currentBrandName={profile.name}
                            relatedBrands={relatedBrands}
                            onBrandClick={handleBrandClick}
                        />
                    </div>
                )}
                {profile.brand_ad && !feedsRedesign && (
                    <div className={styles.brandAdd}>
                        <BrandAdContainer {...profile.brand_ad} />
                    </div>
                )}
                <div className={styles.line} />
            </div>
            {profile.brand_ad && feedsRedesign && (
                <div className={styles.brandAdd}>
                    <BrandAdContainer {...profile.brand_ad} />
                </div>
            )}
        </>
    );
}

export default withReduxProvider(ProfileHeaderWithLogo);
